import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, useThemeMode } from './contexts/ThemeContext';
import { getTheme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoadingScreen from './components/LoadingScreen';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CardTemplates from './pages/CardTemplates';
import CardCreator from './pages/CardCreator';
import CardCustomizer from './pages/CardCustomizer';
import TemplateSelection from './pages/TemplateSelection';
import Analytics from './pages/Analytics';
import PublicCard from './pages/PublicCard';
import AdminPanel from './pages/AdminPanel';
import PublicCardViewer from './pages/PublicCardViewer';
import PersonalCard from './pages/PersonalCard';
import TemplatePreview from './pages/TemplatePreview';
import MyCards from './pages/MyCards';
import NfcManager from './pages/NfcManager';
import NfcPrintDesigns from './pages/NfcPrintDesigns';
import MyNfcPrintDesigns from './pages/MyNfcPrintDesigns';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Subscription from './pages/Subscription';
import QrManager from './pages/QrManager';
import Profile from './pages/Profile';
import Settings from './pages/Settings';

const Register = React.lazy(() => import('./pages/Register'));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    const p = user.plan;
    const isPaid =
      user.role === 'admin' ||
      p === 'premium' ||
      p === 'pro' ||
      p === 'business' ||
      p === 'lifetime';
    return <Navigate to={isPaid ? '/dashboard' : '/my-cards'} />;
  }

  return children;
};

const DefaultRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  const p = user.plan;
  const isPaid =
    user.role === 'admin' ||
    p === 'premium' ||
    p === 'pro' ||
    p === 'business' ||
    p === 'lifetime';
  return <Navigate to={isPaid ? '/dashboard' : '/my-cards'} />;
};

function AppWithTheme() {
  const { mode } = useThemeMode();
  const theme = React.useMemo(() => getTheme(mode), [mode]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <React.Suspense fallback={<LoadingScreen />}>
                  <Register />
                </React.Suspense>
              </PublicRoute>
            } />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/templates" element={
              <ProtectedRoute>
                <Layout>
                  <TemplateSelection />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/cards/templates" element={
              <ProtectedRoute>
                <Layout>
                  <CardTemplates />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/cards/create" element={
              <ProtectedRoute>
                <Layout>
                  <CardCreator />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/customize" element={
              <ProtectedRoute>
                <Layout>
                  <CardCustomizer />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <Layout>
                  <Analytics />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/my-cards" element={
              <ProtectedRoute>
                <Layout>
                  <MyCards />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/nfc" element={
              <ProtectedRoute>
                <Layout>
                  <NfcManager />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/nfc/print-designs" element={
              <ProtectedRoute>
                <Layout>
                  <NfcPrintDesigns />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/nfc/my-print-designs" element={
              <ProtectedRoute>
                <Layout>
                  <MyNfcPrintDesigns />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/qr-codes" element={
              <ProtectedRoute>
                <Layout>
                  <QrManager />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/subscription" element={
              <ProtectedRoute>
                <Layout>
                  <Subscription />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <Layout>
                  <AdminPanel />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/preview-template/:templateId" element={<TemplatePreview />} />
            <Route path="/card/:cardUrl" element={<PublicCard />} />
            <Route path="/public/card/:cardId" element={<PublicCardViewer />} />
            <Route path="/:username" element={<PersonalCard />} />
          </Routes>
        </Router>
      </AuthProvider>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppWithTheme />
    </ThemeProvider>
  );
}

export default App;
